import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../context/AuthContext'
import AdminLayout from '../components/AdminLayout'
import { sendInviteEmail, createStaffUser } from '../../../lib/emailService'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

const generatePassword = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let password = ''
  for (let i = 0; i < 10; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return password
}

export default function AdminInvite() {
  const { user } = useAuth()
  const [chapters, setChapters] = useState([])
  const [invites, setInvites] = useState([])
  const [activeCoordinators, setActiveCoordinators] = useState([])
  const [formData, setFormData] = useState({ email: '', chapter_id: '' })
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [resending, setResending] = useState(null)
  const [deleteModal, setDeleteModal] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const fetchData = async () => {
    const { data: chaptersData } = await supabase
      .from('chapters').select('*').eq('club_id', CLUB_ID).order('name')
    const { data: invitesData } = await supabase
      .from('invites').select('*, chapters(name)')
      .eq('role', 'chapter_coordinator')
      .eq('accepted', false)
      .order('created_at', { ascending: false })
    const { data: activeData } = await supabase
      .from('invites').select('*, chapters(name)')
      .eq('role', 'chapter_coordinator')
      .eq('accepted', true)
      .order('created_at', { ascending: false })
    if (chaptersData) setChapters(chaptersData)
    if (invitesData) setInvites(invitesData)
    if (activeData) setActiveCoordinators(activeData)
  }

  useEffect(() => { fetchData() }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(false)
    if (!formData.chapter_id) { setError('Please select a chapter'); setSaving(false); return }

    try {
      const tempPassword = generatePassword()

      const authResult = await createStaffUser(formData.email, tempPassword)
      if (!authResult.success) {
        setError(`Failed to create account: ${authResult.error}`)
        setSaving(false)
        return
      }

      const { error: inviteError } = await supabase.from('invites').insert({
        email: formData.email,
        role: 'chapter_coordinator',
        club_id: CLUB_ID,
        chapter_id: formData.chapter_id,
        invited_by: user.id,
      })
      if (inviteError) { setError(inviteError.message); setSaving(false); return }

      const chapter = chapters.find(c => c.id === formData.chapter_id)
      const { data: clubData } = await supabase.from('clubs').select('name').eq('id', CLUB_ID).single()

      const result = await sendInviteEmail({
        email: formData.email,
        role: 'chapter_coordinator',
        clubName: clubData?.name || 'MWFC',
        chapterName: chapter?.name || '',
        tempPassword,
      })

      if (!result.success) {
        setError(`Account created but email failed: ${result.error || 'Unknown error'}`)
        setSaving(false)
        return
      }

      setSuccess(true)
      setFormData({ email: '', chapter_id: '' })
      fetchData()
    } catch (err) {
      setError(`Failed to send invite: ${err.message}`)
    }
    setSaving(false)
  }

  const handleResend = async (invite) => {
    setResending(invite.id)
    try {
      const { data: clubData } = await supabase.from('clubs').select('name').eq('id', CLUB_ID).single()
      await sendInviteEmail({
        email: invite.email,
        role: 'chapter_coordinator',
        clubName: clubData?.name || 'MWFC',
        chapterName: invite.chapters?.name || '',
        tempPassword: null,
      })
    } catch (err) {
      console.error('Resend failed:', err)
    }
    setResending(null)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return
    setDeleting(true)
    const { error } = await supabase.from('invites').delete().eq('id', deleteModal.id)
    if (error) { alert(`Failed to cancel: ${error.message}`); setDeleting(false); return }
    setDeleting(false)
    setDeleteModal(null)
    fetchData()
  }

  const inputClass = "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  return (
    <AdminLayout>
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">Invite Coordinator</h1>
        <p className="text-gray-500 text-sm">Assign a chapter coordinator to manage a regional branch</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">

        {/* Invite Form */}
        <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6 sm:p-8">
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">New Invite</div>
          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm mb-4">
              Invite sent! The coordinator will receive an email with their login credentials.
            </div>
          )}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Email Address</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="coordinator@chapter.com" required className={inputClass} />
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Assign to Chapter</label>
              <select value={formData.chapter_id} onChange={(e) => setFormData({ ...formData, chapter_id: e.target.value })} required className={inputClass}>
                <option value="">Select a chapter...</option>
                {chapters.map((chapter) => <option key={chapter.id} value={chapter.id}>{chapter.name}</option>)}
              </select>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 px-4 py-3 text-xs text-blue-600 dark:text-blue-400">
              A temporary password will be auto-generated and sent to the coordinator via email. They can change it after first login.
            </div>
            <button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors mt-2">
              {saving ? 'Creating Account & Sending...' : 'Send Invite'}
            </button>
          </form>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">

          {/* Pending Invites */}
          <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6 sm:p-8">
            <div className="flex justify-between items-center mb-6">
              <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Pending Invites</div>
              {invites.length > 0 && <div className="text-xs text-gray-400">{invites.length} pending</div>}
            </div>
            {invites.length === 0 ? (
              <div className="text-center py-6">
                <div className="text-gray-300 dark:text-white/10 text-4xl mb-3">✉</div>
                <div className="text-gray-400 text-sm">No pending invites</div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {invites.map((invite) => (
                  <div key={invite.id} className="border border-gray-200 dark:border-white/10 p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-sm text-gray-900 dark:text-white">{invite.email}</div>
                        <div className="text-gray-500 text-xs mt-0.5">{invite.chapters?.name} Chapter</div>
                      </div>
                      <span className="text-gray-400 text-xs shrink-0 ml-2">
                        {new Date(invite.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-white/5">
                      <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 mr-auto">Pending</span>
                      <button onClick={() => handleResend(invite)} disabled={resending === invite.id} className="text-xs font-bold uppercase tracking-widest text-green-600 dark:text-green-400 hover:text-green-700 transition-colors disabled:opacity-50">
                        {resending === invite.id ? 'Sending...' : 'Resend'}
                      </button>
                      <span className="text-gray-300 dark:text-white/20">|</span>
                      <button onClick={() => setDeleteModal(invite)} className="text-xs font-bold uppercase tracking-widest text-red-500 hover:text-red-400 transition-colors">Cancel</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active Coordinators */}
          <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6 sm:p-8">
            <div className="flex justify-between items-center mb-6">
              <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Active Coordinators</div>
              {activeCoordinators.length > 0 && <div className="text-xs text-gray-400">{activeCoordinators.length} active</div>}
            </div>
            {activeCoordinators.length === 0 ? (
              <div className="text-center py-6">
                <div className="text-gray-400 text-sm">No coordinators registered yet</div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {activeCoordinators.map((invite) => (
                  <div key={invite.id} className="flex items-center justify-between border border-gray-200 dark:border-white/10 p-4">
                    <div>
                      <div className="font-semibold text-sm text-gray-900 dark:text-white">{invite.email}</div>
                      <div className="text-gray-500 text-xs mt-0.5">{invite.chapters?.name} Chapter</div>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 shrink-0">
                      Active
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Cancel Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center px-4">
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 w-full max-w-sm p-8">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 flex items-center justify-center mx-auto mb-5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-500">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <h2 className="font-black text-lg uppercase tracking-tight text-center text-gray-900 dark:text-white mb-2">Cancel Invite</h2>
            <p className="text-gray-500 text-sm text-center leading-relaxed mb-2">Cancel the pending invite for</p>
            <p className="font-black text-center text-gray-900 dark:text-white text-sm mb-1">{deleteModal.email}</p>
            <p className="text-gray-400 text-xs text-center mb-6">{deleteModal.chapters?.name} Chapter</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal(null)} className="flex-1 border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 py-3 text-xs font-bold uppercase tracking-widest transition-colors">Keep Invite</button>
              <button onClick={handleDeleteConfirm} disabled={deleting} className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors">
                {deleting ? 'Cancelling...' : 'Cancel Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}